/**
 * HealthFacilitiesList — /health/hospitals, /health/clinics,
 * /health/pharmacies. Reused across facility types via prop.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Loader2, PlusCircle, Building2 } from "lucide-react";
import { http } from "@/lib/api";
import HealthCategoryNav from "@/components/HealthCategoryNav";

export default function HealthFacilitiesList({ facilityType, label }) {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const country  = params.get("country") || "";
  const city     = params.get("city") || "";
  const q        = params.get("q") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { http.get("/health/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const p = { facility_type: facilityType };
    if (category) p.category = category;
    if (country)  p.country  = country;
    if (city)     p.city     = city;
    if (q)        p.q        = q;
    http.get("/health/facilities", { params: p })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [facilityType, category, country, city, q]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const categoryOptions = facilityType === "hospital"
    ? (meta?.hospital_types || [])
    : facilityType === "clinic"
      ? (meta?.clinic_categories || [])
      : facilityType === "laboratory"
        ? (meta?.laboratory_types || [])
        : [];

  return (
    <>
      <HealthCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid={`facilities-list-${facilityType}`}>
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="overline mb-1">{label.toUpperCase()}</div>
            <h1 className="font-display text-4xl mb-1">{label}</h1>
            <p className="opacity-70">{total} listed</p>
          </div>
        </div>

        <div className="wofy-card !p-3 mb-6 flex flex-wrap gap-2">
          {categoryOptions.length > 0 && (
            <select value={category} onChange={e => setFilter("category", e.target.value)}
              className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
              data-testid="filter-category">
              <option value="">All categories</option>
              {categoryOptions.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
          )}
          <input value={country} onChange={e => setFilter("country", e.target.value)}
            placeholder="Country" className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <input value={city} onChange={e => setFilter("city", e.target.value)}
            placeholder="City" className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <input value={q} onChange={e => setFilter("q", e.target.value)}
            placeholder="Search…" className="text-sm px-2 py-1 rounded border flex-1 min-w-[8rem]"
            style={{ borderColor: "var(--wofy-border)" }} />
        </div>

        {loading ? (
          <div className="opacity-60 text-sm inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center opacity-70">
            No {label.toLowerCase()} match your filters yet.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(f => (
              <Link key={f.id} to={`/health/facilities/${f.slug}`}
                className="wofy-card !p-4 hover:shadow-md transition block"
                data-testid={`facility-card-${f.slug}`}>
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">
                  {f.facility_type}{f.category ? ` · ${f.category}` : ""}
                  {f.emergency && <span className="ml-2" style={{ color: "#DC2626" }}>· 24/7 Emergency</span>}
                </div>
                <div className="font-medium mb-1 line-clamp-2">{f.name}</div>
                {f.description && <p className="text-sm opacity-80 line-clamp-2">{f.description}</p>}
                {(f.city || f.country) && (
                  <div className="text-xs opacity-70 flex items-center gap-1 mt-2">
                    <MapPin className="w-3 h-3"/>{[f.city, f.state, f.country].filter(Boolean).join(", ")}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
